import { motion } from "framer-motion";
import {
  Sparkles,
  MousePointerClick,
  Zap,
  LayoutTemplate,
  SlidersHorizontal,
  Plug,
  Server,
  Users,
  Wallet,
  Target,
  Check,
} from "lucide-react";
import { Chapter, SectionTitle, SectionSub, LovableLogo, EmergentLogo, EASE } from "./shared";

const ROWS = [
  { icon: Sparkles, key: "ai-quality", label: "AI Quality", lov: "Superb frontend & UI generation", emg: "Multi-agent full-stack reasoning", win: "emg" },
  { icon: MousePointerClick, key: "ease-of-use", label: "Ease of Use", lov: "Chat + visual point-and-click editor", emg: "Plain-language agent chat", win: "lov" },
  { icon: Zap, key: "speed", label: "Speed", lov: "First preview in ~2 minutes", emg: "Full-stack app in ~4 minutes", win: "lov" },
  { icon: LayoutTemplate, key: "templates", label: "Templates", lov: "Large community gallery", emg: "Curated full-stack starters", win: "lov" },
  { icon: SlidersHorizontal, key: "customization", label: "Customization", lov: "Visual edits + code export", emg: "Unlimited, full code control", win: "emg" },
  { icon: Plug, key: "integrations", label: "Integrations", lov: "Supabase, GitHub, Stripe, Figma", emg: "Any API, LLMs, databases, payments", win: "emg" },
  { icon: Server, key: "hosting", label: "Hosting", lov: "One-click publish, custom domains", emg: "Managed deploys, custom domains", win: "tie" },
  { icon: Users, key: "collaboration", label: "Collaboration", lov: "Shared team workspaces", emg: "Teams + GitHub sync", win: "tie" },
  { icon: Wallet, key: "pricing", label: "Pricing", lov: "Free tier, Pro from $25/mo", emg: "Free tier, Standard plan $20/mo", win: "emg" },
  { icon: Target, key: "best-for", label: "Best For", lov: "Web UIs, MVPs & landing pages", emg: "Full-stack web + mobile apps", win: "tie" },
];

const Cell = ({ text, win, side, testId }) => (
  <div
    data-testid={testId}
    className={`rounded-xl px-4 py-3.5 text-sm transition-colors duration-300 ${
      win
        ? side === "lov"
          ? "bg-[#FF2D55]/[0.07] text-white"
          : "bg-cyan-400/[0.07] text-white"
        : "text-slate-400"
    }`}
  >
    <span
      className={`mb-1.5 flex items-center gap-1.5 font-mono2 text-[10px] uppercase tracking-[0.25em] md:hidden ${
        side === "lov" ? "text-[#FF8FA8]" : "text-cyan-300/80"
      }`}
    >
      {side === "lov" ? "Lovable" : "Emergent"}
    </span>
    <span className="flex items-start gap-2.5">
      <Check
        className={`mt-0.5 h-4 w-4 shrink-0 ${
          win ? (side === "lov" ? "text-[#FF5C8A]" : "text-cyan-300") : "text-slate-600"
        }`}
      />
      <span className="leading-snug">{text}</span>
    </span>
  </div>
);

export default function ComparisonTable() {
  return (
    <section id="comparison" className="relative py-11">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Chapter index="02" label="Head-to-Head Matrix" testId="comparison-chapter-badge" />
        <SectionTitle>
          The full comparison, <span className="text-gradient-cyan">feature by feature</span>
        </SectionTitle>
        <SectionSub>
          Ten categories that actually matter when you ship real products. Highlighted cells mark
          the stronger tool in each row.
        </SectionSub>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: EASE }}
          className="mt-10 overflow-x-auto rounded-3xl border border-white/10 bg-[#0A0A0A]/80 backdrop-blur-xl"
        >
          <div>
            <div className="relative hidden grid-cols-[1.05fr_1fr_1fr] border-b border-white/15 bg-[#0C0C0C] px-6 py-6 md:grid">
              <span className="font-mono2 self-center pl-11 text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
                Features
              </span>
              <div className="flex items-center gap-3 pl-4">
                <LovableLogo size={32} />
                <span className="font-display text-lg font-extrabold text-white">Lovable</span>
              </div>
              <div className="flex items-center gap-3 pl-4">
                <EmergentLogo size={32} />
                <span className="font-display text-lg font-extrabold text-white">Emergent</span>
              </div>
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-[#FF2D55]/40 via-white/10 to-cyan-400/40" />
            </div>

            {ROWS.map((row, i) => (
              <motion.div
                key={row.key}
                data-testid={`comparison-row-${row.key}`}
                initial={{ opacity: 0, x: -24 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.55, delay: i * 0.04, ease: EASE }}
                className={`grid grid-cols-1 gap-y-2 px-5 py-4 transition-colors duration-300 hover:bg-white/[0.03] md:grid-cols-[1.05fr_1fr_1fr] md:items-center md:gap-x-4 md:gap-y-0 md:px-6 md:py-2.5 ${
                  i !== ROWS.length - 1 ? "border-b border-white/[0.06]" : ""
                } ${i % 2 === 1 ? "bg-white/[0.015]" : ""}`}
              >
                <div className="flex items-center gap-3 py-2">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-white/10 bg-white/[0.04]">
                    <row.icon className="h-4 w-4 text-cyan-300/80" />
                  </span>
                  <span className="text-sm font-semibold text-white">{row.label}</span>
                </div>
                <Cell
                  text={row.lov}
                  win={row.win === "lov"}
                  side="lov"
                  testId={`comparison-cell-lovable-${row.key}`}
                />
                <Cell
                  text={row.emg}
                  win={row.win === "emg"}
                  side="emg"
                  testId={`comparison-cell-emergent-${row.key}`}
                />
              </motion.div>
            ))}
          </div>
        </motion.div>

      </div>
    </section>
  );
}
